/* tslint:disable:max-file-line-count */
import { mergeStyles } from '@cian/utils';
import throttle from 'lodash/throttle';
import * as React from 'react';

import { ImageLoad } from './image_load';
import styles from './index.css';
import { INeighbourhoodSchema } from '../../../types';
import { imageSize, isArrayWithItems } from '../../../utils/helpers';
import { IParsedSlides, parseSlides } from '../../../utils/parseSlides';
import { ISize } from '../../mobile/slider/types';
import { Navigation } from '../navigation';

// 60% от высоты экрана
const SLIDE_HEIGHT = 0.68;

export interface ISliderProps {
  slides: INeighbourhoodSchema[];
  currentSlide: number;
}

export interface ISliderState {
  currentSlide: number;
  translate?: number;
  trackCss?: React.CSSProperties;
  withAnimation?: boolean;
  animated?: boolean;
  trackWidth?: number;
}

export class Slider extends React.Component<ISliderProps, ISliderState> {
  private parsedSlides: IParsedSlides;
  private container: HTMLDivElement;
  private containerWidth = 0;
  private containerHeight = 0;
  private resizeThrottleFn: Function;

  public constructor(props: ISliderProps) {
    super(props);
    this.parsedSlides = parseSlides(props.slides);

    this.state = {
      animated: false,
      currentSlide: props.currentSlide || 0,
      translate: 0,
      withAnimation: false,
    };
  }

  public get slidesLength() {
    return isArrayWithItems(this.parsedSlides.slides) ? this.parsedSlides.slides.length : 0;
  }

  public get lastSlideIndex() {
    return this.slidesLength - 1;
  }

  public componentDidMount() {
    const { trackWidth, translate } = this.calculateSizes(this.props.slides.length, this.props.currentSlide);
    this.setState({
      trackWidth,
      translate,
    });
    window.addEventListener('resize', this.onResizeHandler);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: ISliderProps) {
    if (this.props.slides !== nextProps.slides) {
      this.parsedSlides = parseSlides(nextProps.slides);
      const { trackWidth, translate } = this.calculateSizes(nextProps.slides.length, nextProps.currentSlide);
      this.setState({
        currentSlide: nextProps.currentSlide,
        trackWidth,
        translate,
        withAnimation: false,
      });
    }
  }

  public prev = () => {
    const { animated, currentSlide } = this.state;
    if (!animated) {
      this.setCurrentSlide(currentSlide === 0 ? this.lastSlideIndex : currentSlide - 1);
    }
  };

  public next = () => {
    const { animated, currentSlide } = this.state;
    if (!animated) {
      this.setCurrentSlide(currentSlide === this.lastSlideIndex ? 0 : currentSlide + 1);
    }
  };

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResizeHandler);
  }

  public render() {
    if (this.props.slides.length === 1) {
      return this.renderOneSlide();
    }
    const { currentSlide } = this.state;

    return (
      <div>
        <Navigation
          slides={this.parsedSlides.previews}
          currentSlide={currentSlide}
          onChangeSlide={index => this.setCurrentSlide(index)}
          slideOffset={4}
        />
        {this.renderSlider()}
      </div>
    );
  }

  private renderOneSlide() {
    const { slide, sizes } = this.parsedSlides.slides[0];

    return (
      <div>
        <Navigation
          slides={this.parsedSlides.previews}
          currentSlide={0}
          onChangeSlide={() => this.setCurrentSlide(0)}
          slideOffset={2}
        />
        <div className={styles['container']} ref={el => el && (this.container = el)}>
          <div className={styles['slide_container']} style={{ width: this.containerWidth }}>
            <div className={styles['slide']} style={{ height: this.containerHeight }}>
              <ImageLoad src={slide} onLoad={e => !sizes && this.imageOnLoad(e, 0)} style={this.getSizeStyle(sizes)} />
            </div>
            <div className={styles['description']}>{this.parsedSlides.slides[0].description}</div>
          </div>
        </div>
      </div>
    );
  }

  private renderSlider() {
    const { currentSlide, translate, withAnimation } = this.state;
    const lastSlideIndex = this.lastSlideIndex;
    if (lastSlideIndex === -1) {
      return <div />;
    }

    return (
      <div className={styles['container']} ref={el => el && (this.container = el)}>
        <div
          className={styles['track']}
          style={{
            transform: `translateX(${translate}px)`,
            transition: withAnimation ? `.5s ease-out` : '',
            width: this.state.trackWidth,
          }}
        >
          {this.renderSlide(lastSlideIndex)}
          {this.props.slides.map((_, index) => {
            if (
              index === currentSlide ||
              index === currentSlide - 1 ||
              index === (currentSlide + 1) % (lastSlideIndex + 1)
            ) {
              this.parsedSlides.slides[index].loaded = true;
            }

            return this.renderSlide(index);
          })}
          {this.renderSlide(lastSlideIndex + 1)}
        </div>
        {this.renderNavigationButtons()}
      </div>
    );
  }

  private renderSlide(index: number) {
    const slide = this.parsedSlides.slides[index - 1];
    if (!slide) {
      return null;
    }

    return (
      <div key={`slide-${index}`} className={styles['slide_container']} style={{ width: this.containerWidth }}>
        <div className={styles['slide']} style={{ height: this.containerHeight }}>
          {slide.loaded && <ImageLoad src={slide.slide} onLoad={e => !slide.sizes && this.imageOnLoad(e, index)} />}
        </div>
        <div className={styles['description']}>{slide.description}</div>
      </div>
    );
  }

  private renderNavigationButtons() {
    return (
      <div>
        <button
          {...mergeStyles(styles['button'], styles['button--left'])}
          onClick={this.prev}
          style={{ top: this.containerHeight / 2 }}
        />
        <button
          {...mergeStyles(styles['button'], styles['button--right'])}
          onClick={this.next}
          style={{ top: this.containerHeight / 2 }}
        />
      </div>
    );
  }

  private imageOnLoad(e: Event, index: number) {
    if (!this.parsedSlides.slides[index]) {
      return;
    }
    const imageNode = e.currentTarget as HTMLImageElement;
    const { width, height } = imageNode.getBoundingClientRect();
    // 400 - высота взятая из стилей
    this.parsedSlides.slides[index].sizes = imageSize(
      width,
      height,
      this.containerWidth,
      Math.max(400, this.containerHeight),
    );
  }

  private calculateTranslate(index: number): number {
    return this.containerWidth * index * -1 - this.containerWidth;
  }

  private calculateSizes(
    slidesLength: number,
    currentSlideIndex: number,
  ): {
    trackWidth: number;
    translate: number;
  } {
    if (!this.container) {
      return { trackWidth: 0, translate: 0 };
    }
    this.containerWidth = this.container.getBoundingClientRect().width;
    this.containerHeight = window.innerHeight * SLIDE_HEIGHT;

    return {
      trackWidth: this.containerWidth * (slidesLength + 2) + (slidesLength + 2),
      translate: this.containerWidth * (currentSlideIndex + 1) * -1,
    };
  }

  private setCurrentSlide(index: number) {
    if (this.state.animated) {
      return;
    }
    this.setState(
      {
        animated: true,
        currentSlide: index,
        translate: this.calculateTranslate(index),
        withAnimation: true,
      },
      () => this.setState({ animated: false }),
    );
  }

  private onResizeHandler = () => {
    if (!isArrayWithItems(this.props.slides)) {
      return;
    }
    if (!this.resizeThrottleFn) {
      this.resizeThrottleFn = throttle(
        () =>
          this.setState(
            {
              translate: 0,
            },
            () => {
              const { trackWidth, translate } = this.calculateSizes(this.props.slides.length, this.props.currentSlide);
              this.parsedSlides.slides.forEach(slide => {
                if (!slide || !slide.loaded || !slide.sizes) {
                  return;
                }
                const { width, height } = slide.sizes;
                slide.sizes = imageSize(width, height, this.containerWidth, Math.max(400, this.containerHeight));
              });
              this.setState({
                currentSlide: 0,
                trackWidth,
                translate,
              });
            },
          ),
        400,
      );
    }

    this.resizeThrottleFn();
  };

  private getSizeStyle = (size?: ISize) => ({
    height: (size && size.height) || '',
    width: (size && size.width) || '',
  });
}
